import { useState } from 'react';
import { LensSymbol, LensWordmark } from '@/components/Brand';
import { Navigate } from 'react-router-dom';
import { authErrorText, useAuth } from '@/data/auth';

/**
 * 로그인.
 *
 * ★ 진입 화면이다. **가입 링크는 없다** — 1인 운영이고 계정은 콘솔에서 발급한다.
 *
 * ⚠️ 로고는 아직 벡터로 옮기지 않았다. Flutter 쪽은 `LensLogo`(CustomPainter)와
 * base64 앱 아이콘을 썼는데, **DSL 에 에셋 업로드 API 가 없어서** 그렇게 된
 * 것이다 — ★ 그 제약은 여기서 사라지므로 **파일로 두면 된다**(⑧에서).
 */
export function LoginPage() {
  const { user, loading, signIn, sendReset } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  // ★ 재설정 안내는 **오류와 따로** 둔다 — 성공 메시지를 빨간 칸에 넣으면
  //   읽는 사람이 실패로 읽는다.
  const [notice, setNotice] = useState('');

  if (loading) {
    return <Center>확인 중…</Center>;
  }
  if (user !== null) return <Navigate to="/" replace />;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await signIn(email.trim(), password);
    } catch (err) {
      // ★ 실패 이유를 **화면에 쓴다.** Flutter 쪽에서는 못 하던 것이다.
      setError(authErrorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Center>
      <form
        onSubmit={submit}
        className="w-full max-w-[380px] rounded-lg border border-border bg-card p-xxl shadow-panel"
      >
        {/* ★ 로그인 화면은 **라이브에서 처음 보이는 화면**이다 — 로고를 크게. */}
        {/* ⚠️ 부제를 뺐다(사용자 지시) — ★ 아래 여백을 머리가 직접 진다. */}
        <h1 className="mb-xxl flex items-center gap-lg">
          <LensSymbol size={52} className="shrink-0" />
          <LensWordmark height={30} />
        </h1>


        <label className="block text-label-md text-muted-foreground" htmlFor="email">
          이메일
        </label>
        <input
          id="email"
          type="email"
          autoComplete="username"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="mt-xs mb-lg w-full rounded-md border border-border bg-[var(--lens-bg)] px-md py-sm text-body-md"
        />

        <label className="block text-label-md text-muted-foreground" htmlFor="password">
          비밀번호
        </label>
        <input
          id="password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="mt-xs mb-xl w-full rounded-md border border-border bg-[var(--lens-bg)] px-md py-sm text-body-md"
        />

        {error !== '' ? (
          <p
            className="mb-lg rounded-md px-md py-sm text-body-sm"
            style={{ background: 'var(--lens-accent-error)', color: 'var(--lens-error)' }}
          >
            {error}
          </p>
        ) : null}

        <button
          type="submit"
          disabled={busy}
          className="w-full rounded-md bg-primary px-lg py-md text-title-sm text-primary-foreground disabled:opacity-60"
        >
          {busy ? '로그인 중…' : '로그인'}
        </button>

        {notice !== '' ? (
          <p className="mt-lg text-body-sm text-muted-foreground">{notice}</p>
        ) : null}
        {/* ★★★ 비밀번호 재설정. ⚠️ 없으면 **푸는 길이 서비스 계정 키뿐**이고
            운영자가 한 명이라 아무도 풀어줄 사람이 없다(2026-10-09 실제로 막혔다).
            ★ 재설정 화면은 Firebase 가 호스팅하는 것을 쓴다 — LENS 가 안 만든다. */}
        <button
          type="button"
          disabled={busy}
          onClick={async () => {
            const to = email.trim();
            if (to === '') {
              setError('이메일을 먼저 입력하십시오.');
              return;
            }
            setBusy(true);
            setError('');
            setNotice('');
            try {
              await sendReset(to);
              // ⚠️ **계정이 있는지 말하지 않는다** — 없는 주소를 넣어 계정
              //    존재 여부를 알아내는 것을 막는다. Firebase 도 같은 이유로
              //    없는 주소에 오류를 안 낸다.
              setNotice(`${to} 로 재설정 메일을 보냈습니다. 받은 편지함을 확인하십시오.`);
            } catch (err) {
              setError(authErrorText(err));
            } finally {
              setBusy(false);
            }
          }}
          className="mt-md w-full text-body-sm text-muted-foreground underline underline-offset-4 disabled:opacity-60"
        >
          비밀번호를 잊으셨습니까?
        </button>
        <p className="mt-lg text-body-sm text-muted-foreground">
          계정은 운영자가 발급합니다. 가입 화면은 열려 있지 않습니다.
        </p>
      </form>
    </Center>
  );
}

function Center({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-gutter">
      {children}
    </div>
  );
}

/**
 * 인증 게이트.
 *
 * ⚠️ `loading` 동안 로그인 화면으로 보내지 않는다 — 새로고침마다 **깜빡인다.**
 */
export function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <Center>확인 중…</Center>;
  if (user === null) return <Navigate to="/login" replace />;
  return <>{children}</>;
}
